import { defineStore } from 'pinia'

let _recognitionInstance = null
let _silenceTimer = null
const SILENCE_TIMEOUT = 3000

// Indonesian spoken punctuation → symbol mapping
// Order matters: longer phrases must come before shorter ones to avoid partial matches
const PUNCTUATION_MAP = [
  // Multi-word phrases first
  { pattern: /\btitik dua\b/gi, replacement: ':' },
  { pattern: /\btitik koma\b/gi, replacement: ';' },
  { pattern: /\btitik tiga\b/gi, replacement: '...' },
  { pattern: /\bbaris baru\b/gi, replacement: '\n' },
  { pattern: /\bgaris miring\b/gi, replacement: '/' },
  { pattern: /\btanda seru\b/gi, replacement: '!' },
  { pattern: /\btanda tanya\b/gi, replacement: '?' },
  { pattern: /\bkurung buka\b/gi, replacement: '(' },
  { pattern: /\bkurung tutup\b/gi, replacement: ')' },
  { pattern: /\bstrip strip\b/gi, replacement: '—' },
  // Single-word
  { pattern: /\bkoma\b/gi, replacement: ',' },
  { pattern: /\btitik\b/gi, replacement: '.' },
  { pattern: /\bkali\b/gi, replacement: 'x' },
  { pattern: /\bstrip\b/gi, replacement: '-' },
  { pattern: /\bpersen\b/gi, replacement: '%' },
  { pattern: /\bplus\b/gi, replacement: '+' },
  { pattern: /\btambah\b/gi, replacement: '+' },
  { pattern: /\bkurang\b/gi, replacement: '-' },
  { pattern: /\bbagi\b/gi, replacement: '/' },
  { pattern: /\bper\b/gi, replacement: '/' },
  { pattern: /\bspasi\b/gi, replacement: ' ' },
]

function applyPunctuation(text) {
  if (!text) return text
  let result = text
  for (const { pattern, replacement } of PUNCTUATION_MAP) {
    result = result.replace(pattern, replacement)
  }
  // Clean up extra spaces around punctuation (except newlines)
  result = result.replace(/ +([.,;:!?%)x\-+])/g, '$1')
  result = result.replace(/([(\n]) +/g, '$1')
  return result.trim()
}

export const useSpeechInputStore = defineStore({
  id: 'speechInput',
  state: () => ({
    showModal: false,
    isListening: false,
    transcript: '',
    interimTranscript: '',
    status: 'idle', // 'idle' | 'requesting_permission' | 'listening' | 'stopping' | 'error'
    errorMessage: '',
    _resolve: null,
    _reject: null,
  }),
  getters: {
    displayTranscript(state) {
      return state.transcript || state.interimTranscript || ''
    },
    isSupported() {
      return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window
    },
  },
  actions: {
    requestSpeech() {
      if (this.showModal && this._resolve) {
        return new Promise((resolve) => {
          const prev = this._resolve
          this._resolve = (v) => { prev(v); resolve(v) }
        })
      }
      this.transcript = ''
      this.interimTranscript = ''
      this.errorMessage = ''
      this.status = 'idle'
      this.showModal = true
      return new Promise((resolve, reject) => {
        this._resolve = resolve
        this._reject = reject
      })
    },

    async startListening() {
      // Guard against concurrent calls (e.g. double-click during getUserMedia await)
      if (this.isListening || this.status === 'requesting_permission') return

      const SpeechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition
      if (!SpeechRecognition) {
        this.status = 'error'
        this.errorMessage = 'Browser tidak mendukung Speech Recognition'
        return
      }

      this.status = 'requesting_permission'
      // Pre-check microphone permission
      try {
        if (navigator.permissions && navigator.permissions.query) {
          const permResult = await navigator.permissions.query({ name: 'microphone' })
          if (permResult.state === 'denied') {
            this.status = 'error'
            this.errorMessage = 'Akses mikrofon diblokir. Buka pengaturan browser untuk mengizinkan akses mikrofon.'
            return
          }
        }
        // Request mic access to trigger browser permission prompt if needed
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        // Stop tracks immediately — we only needed the permission grant
        stream.getTracks().forEach(track => track.stop())
      } catch (err) {
        this.status = 'error'
        this.errorMessage = 'Akses mikrofon ditolak. Izinkan akses mikrofon di pengaturan browser.'
        return
      }

      // Abort guard: if user cancelled while we were awaiting permission
      if (this.status !== 'requesting_permission') return

      const recognition = new SpeechRecognition()
      recognition.lang = 'id-ID'
      recognition.continuous = true
      recognition.interimResults = true

      recognition.onresult = (event) => {
        this._resetSilenceTimer()
        let finalText = this.transcript // preserve already-committed finals
        let interimText = ''
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i]
          if (result.isFinal) {
            finalText += result[0].transcript
          } else {
            interimText += result[0].transcript
          }
        }
        this.transcript = finalText
        this.interimTranscript = interimText
      }

      recognition.onerror = (event) => {
        if (event.error === 'no-speech') {
          this._handleSilenceTimeout()
          return
        }
        if (event.error === 'not-allowed') {
          this.status = 'error'
          this.errorMessage = 'Akses mikrofon ditolak. Izinkan akses mikrofon di pengaturan browser.'
          this._clearSilenceTimer()
          return
        }
        if (event.error === 'aborted') return
        this.status = 'error'
        this.errorMessage = 'Terjadi kesalahan: ' + event.error
        this._clearSilenceTimer()
      }

      recognition.onend = () => {
        this._clearSilenceTimer()
        if (this.status === 'stopping' || this.status === 'listening') {
          const finalText = (this.transcript || this.interimTranscript || '').trim()
          this._resolveWithText(finalText)
        }
        this.isListening = false
        _recognitionInstance = null
      }

      _recognitionInstance = recognition
      recognition.start()
      this.isListening = true
      this.status = 'listening'
      this._resetSilenceTimer()
    },

    stopListening() {
      this.status = 'stopping'
      this._clearSilenceTimer()
      if (_recognitionInstance) {
        _recognitionInstance.stop()
      }
    },

    _resolveWithText(text) {
      this.showModal = false
      this.status = 'idle'
      this.isListening = false
      if (this._resolve) {
        this._resolve(applyPunctuation(text) || null)
        this._resolve = null
        this._reject = null
      }
    },

    cancel() {
      this._clearSilenceTimer()
      if (_recognitionInstance) {
        try { _recognitionInstance.abort() } catch (_) { /* ignore */ }
        _recognitionInstance = null
      }
      this.showModal = false
      this.status = 'idle'
      this.isListening = false
      this.transcript = ''
      this.interimTranscript = ''
      if (this._resolve) {
        this._resolve(null)
        this._resolve = null
        this._reject = null
      }
    },

    _resetSilenceTimer() {
      this._clearSilenceTimer()
      _silenceTimer = setTimeout(() => {
        if (this.isListening) {
          this.stopListening()
        }
      }, SILENCE_TIMEOUT)
    },

    _clearSilenceTimer() {
      if (_silenceTimer) {
        clearTimeout(_silenceTimer)
        _silenceTimer = null
      }
    },

    _handleSilenceTimeout() {
      if (this.isListening) {
        this.stopListening()
      }
    },
  },
})
